import { forwardRef, type TextareaHTMLAttributes, useId } from 'react';
import { cn } from './utils';

export type TextareaSize = 'sm' | 'md';
export type TextareaResize = 'none' | 'vertical' | 'both';
export type TextareaVariant = 'default' | 'ghost';

export interface TextareaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> {
  size?: TextareaSize;
  /**
   * Visual variant. `ghost` strips border, background, and padding so
   * the textarea blends into its container (e.g. an inline FAQ answer
   * editor). Consumer owns text size/color via className.
   */
  variant?: TextareaVariant;
  /**
   * - `false` (default): no error state.
   * - `true`: apply error styling only.
   * - `string`: apply error styling and render the message under the
   *   textarea with `role="alert"` and aria-describedby wired.
   */
  error?: boolean | string;
  label?: string;
  helpText?: string;
  /**
   * Classes for the outer wrapper element. Use this for layout that has to
   * apply to the field's box in its parent — e.g. `flex-1 min-w-0` when the
   * field is a flex child. `className` lands on the `<textarea>`, which is
   * not the flex item, so layout classes there have no effect.
   */
  wrapperClassName?: string;
  resize?: TextareaResize;
}

const sizeStyles: Record<TextareaVariant, Record<TextareaSize, string>> = {
  default: {
    md: 'px-4 py-3 rounded-lg text-base',
    sm: 'px-3 py-2 rounded-md text-sm',
  },
  ghost: {
    md: 'p-0',
    sm: 'p-0',
  },
};

const resizeStyles: Record<TextareaResize, string> = {
  none: 'resize-none',
  vertical: 'resize-y',
  both: 'resize',
};

const variantBase: Record<TextareaVariant, string> = {
  default:
    'w-full border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent text-neutral-800',
  ghost:
    'w-full border-0 bg-transparent focus:outline-none focus:ring-0 text-neutral-800 placeholder:text-neutral-300',
};
const errorStyles = 'border-error-border focus:ring-error-fg';
const disabledStyles = 'bg-neutral-50 text-neutral-500 cursor-not-allowed';

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  function Textarea(props, ref) {
    const {
      size = 'md',
      variant = 'default',
      error = false,
      label,
      helpText,
      wrapperClassName,
      resize = 'vertical',
      rows = 4,
      className = '',
      id: providedId,
      disabled,
      required,
      ...rest
    } = props;

    const generatedId = useId();
    const id = providedId || generatedId;
    const helpId = `${id}-help`;
    const errorId = `${id}-error`;
    const errorMessage = typeof error === 'string' ? error : undefined;
    const hasError = Boolean(error);
    const describedBy = [
      errorMessage ? errorId : null,
      helpText && !errorMessage ? helpId : null,
    ]
      .filter(Boolean)
      .join(' ') || undefined;

    const textareaClassName = cn(
      variantBase[variant],
      sizeStyles[variant][size],
      resizeStyles[resize],
      hasError && variant === 'default' && errorStyles,
      disabled && variant === 'default' && disabledStyles,
      className,
    );

    return (
      <div className={wrapperClassName || undefined}>
        {label && (
          <label htmlFor={id} className="block text-sm font-medium text-neutral-600 mb-2">
            {label}
            {required && <span className="text-error-fg ml-0.5" aria-hidden="true">*</span>}
          </label>
        )}
        <textarea
          ref={ref}
          id={id}
          rows={rows}
          className={textareaClassName}
          disabled={disabled}
          required={required}
          aria-invalid={hasError || undefined}
          aria-describedby={describedBy}
          {...rest}
        />
        {helpText && !errorMessage && (
          <p id={helpId} className="text-xs text-neutral-500 mt-1">{helpText}</p>
        )}
        {errorMessage && (
          <p id={errorId} className="text-xs text-error-fg mt-1" role="alert">{errorMessage}</p>
        )}
      </div>
    );
  },
);
